import { useState, useEffect, useCallback } from 'react';
import { getToken } from '../utils/auth';

const API = 'https://7fvzvlypv8.execute-api.us-east-1.amazonaws.com/prod';

const STATUS_COLORS = {
	Pending: { color: '#C88A50', bg: '#221A10', border: '#342410' },
	Approved: { color: '#6DBF67', bg: '#0E1F0E', border: '#162A16' },
	Denied: { color: '#E07070', bg: '#2A1818', border: '#3A2020' },
};

const ROLE_OPTIONS = ['Partner', 'Descendant', 'Researcher'];

export default function AccessRequestsTab({ C, useBreakpoint }) {
	const { isMobile, isTablet, px } = useBreakpoint();
	const compact = isMobile || isTablet;

	const [requests, setRequests] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [filter, setFilter] = useState('Pending');

	// Per-request state
	const [processing, setProcessing] = useState({});
	const [selectedRoles, setSelectedRoles] = useState({});
	const [denyReasons, setDenyReasons] = useState({});
	const [confirmingDeny, setConfirmingDeny] = useState({});

	const loadRequests = useCallback(async () => {
		setLoading(true);
		setError('');
		try {
			const token = await getToken();
			const res = await fetch(`${API}/get-access-requests`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({}),
			});
			const data = await res.json();
			if (!res.ok) throw new Error(data.error || 'Failed to load requests');
			setRequests(data.items || []);
		} catch (err) {
			setError(err.message);
		}
		setLoading(false);
	}, []);

	useEffect(() => {
		loadRequests();
	}, []);

	const handleApprove = async (req) => {
		const role = selectedRoles[req.RequestID] || req.Role || 'Partner';
		setProcessing((prev) => ({ ...prev, [req.RequestID]: 'approving' }));
		try {
			const token = await getToken();
			const res = await fetch(`${API}/process-access-request`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					requestId: req.RequestID,
					action: 'approve',
					email: req.Email,
					name: req.Name,
					role,
				}),
			});
			const data = await res.json();
			if (!res.ok) throw new Error(data.error || 'Failed to approve');
			setRequests((prev) =>
				prev.map((r) =>
					r.RequestID === req.RequestID ? { ...r, Status: 'Approved' } : r,
				),
			);
		} catch (err) {
			setError(err.message);
		}
		setProcessing((prev) => ({ ...prev, [req.RequestID]: null }));
	};

	const handleDeny = async (req) => {
		setProcessing((prev) => ({ ...prev, [req.RequestID]: 'denying' }));
		try {
			const token = await getToken();
			const res = await fetch(`${API}/process-access-request`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					requestId: req.RequestID,
					action: 'deny',
					email: req.Email,
					name: req.Name,
					denyReason: denyReasons[req.RequestID] || '',
				}),
			});
			const data = await res.json();
			if (!res.ok) throw new Error(data.error || 'Failed to deny');
			setRequests((prev) =>
				prev.map((r) =>
					r.RequestID === req.RequestID ? { ...r, Status: 'Denied' } : r,
				),
			);
			setConfirmingDeny((prev) => ({ ...prev, [req.RequestID]: false }));
		} catch (err) {
			setError(err.message);
		}
		setProcessing((prev) => ({ ...prev, [req.RequestID]: null }));
	};

	const filtered = requests.filter(
		(r) => filter === 'All' || r.Status === filter,
	);
	const pendingCount = requests.filter((r) => r.Status === 'Pending').length;

	return (
		<div style={{ paddingTop: 100, minHeight: '100vh' }}>
			<div style={{ padding: `40px ${px} 24px` }}>
				{/* Header */}
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-end',
						flexWrap: 'wrap',
						gap: 16,
						marginBottom: 32,
					}}
				>
					<div>
						<div className='section-label' style={{ marginBottom: 12 }}>
							Admin
						</div>
						<h1
							className='display-heading'
							style={{ fontSize: compact ? 28 : 36, marginBottom: 0 }}
						>
							Access Requests.
						</h1>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
						{pendingCount > 0 && (
							<div
								style={{
									background: '#221A10',
									border: '1px solid #C88A50',
									padding: '6px 14px',
									fontFamily: 'DM Mono',
									fontSize: 10,
									color: '#C88A50',
									letterSpacing: '0.1em',
								}}
							>
								{pendingCount} PENDING
							</div>
						)}
						<button
							onClick={loadRequests}
							disabled={loading}
							style={{
								background: 'none',
								border: '1px solid #1A2E2A',
								cursor: 'pointer',
								padding: '6px 14px',
								fontFamily: 'DM Mono',
								fontSize: 10,
								color: '#4A6A66',
								letterSpacing: '0.1em',
							}}
						>
							{loading ? '...' : '↻ Refresh'}
						</button>
					</div>
				</div>

				{/* Filter tabs */}
				<div style={{ display: 'flex', gap: 2, marginBottom: 24 }}>
					{['Pending', 'Approved', 'Denied', 'All'].map((f) => {
						const sc = STATUS_COLORS[f];
						const isActive = filter === f;
						const count =
							f === 'All'
								? requests.length
								: requests.filter((r) => r.Status === f).length;
						return (
							<button
								key={f}
								onClick={() => setFilter(f)}
								style={{
									padding: '8px 16px',
									fontFamily: 'DM Mono',
									fontSize: 10,
									letterSpacing: '0.08em',
									cursor: 'pointer',
									border: `1px solid ${isActive && sc ? sc.border : '#1A2E2A'}`,
									background:
										isActive && sc
											? sc.bg
											: isActive
												? '#162220'
												: 'transparent',
									color:
										isActive && sc
											? sc.color
											: isActive
												? C.tealLight
												: '#4A6A66',
									transition: 'all 0.15s',
									display: 'flex',
									alignItems: 'center',
									gap: 8,
								}}
							>
								{f}
								<span
									style={{
										background: 'rgba(255,255,255,0.1)',
										borderRadius: 2,
										padding: '1px 5px',
										fontSize: 9,
									}}
								>
									{count}
								</span>
							</button>
						);
					})}
				</div>

				{error && (
					<div
						style={{
							fontFamily: 'DM Mono',
							fontSize: 11,
							color: '#E07070',
							marginBottom: 16,
							letterSpacing: '0.06em',
						}}
					>
						{error}
					</div>
				)}

				{loading && (
					<div
						style={{
							background: '#111E1C',
							border: '1px solid #1A2E2A',
							padding: '48px 32px',
							textAlign: 'center',
						}}
					>
						<div
							style={{
								fontFamily: 'DM Mono',
								fontSize: 10,
								color: '#4A6A66',
								letterSpacing: '0.14em',
							}}
						>
							LOADING REQUESTS...
						</div>
					</div>
				)}

				{!loading && filtered.length === 0 && (
					<div
						style={{
							background: '#111E1C',
							border: '1px solid #1A2E2A',
							padding: '48px 32px',
							textAlign: 'center',
						}}
					>
						<div
							style={{
								fontFamily: 'DM Mono',
								fontSize: 10,
								color: '#4A6A66',
								letterSpacing: '0.14em',
								marginBottom: 8,
							}}
						>
							NO {filter.toUpperCase()} REQUESTS
						</div>
						<p style={{ fontSize: 13, color: '#4A6A66' }}>
							{filter === 'Pending'
								? 'No requests awaiting review.'
								: `No ${filter.toLowerCase()} requests found.`}
						</p>
					</div>
				)}

				{!loading && filtered.length > 0 && (
					<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						{filtered.map((req) => {
							const sc = STATUS_COLORS[req.Status] || STATUS_COLORS.Pending;
							const isPending = req.Status === 'Pending';
							const isProcessing = processing[req.RequestID];
							const isDenyConfirming = confirmingDeny[req.RequestID];

							return (
								<div
									key={req.RequestID}
									style={{
										background: '#111E1C',
										border: '1px solid #1A2E2A',
										borderLeft: `3px solid ${sc.color}`,
									}}
								>
									{/* Request header */}
									<div style={{ padding: compact ? '20px' : '24px 28px' }}>
										<div
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'flex-start',
												flexWrap: 'wrap',
												gap: 16,
												marginBottom: 16,
											}}
										>
											<div>
												<div
													style={{
														fontSize: 16,
														color: 'white',
														fontWeight: 500,
														marginBottom: 4,
													}}
												>
													{req.Name}
												</div>
												<div
													style={{
														fontFamily: 'DM Mono',
														fontSize: 10,
														color: '#4A6A66',
														letterSpacing: '0.08em',
														marginBottom: 4,
													}}
												>
													{req.Email}
												</div>
												{req.Organization && (
													<div style={{ fontSize: 12, color: '#6A8A86' }}>
														{req.Organization}
													</div>
												)}
											</div>
											<div
												style={{
													display: 'flex',
													gap: 8,
													alignItems: 'center',
													flexWrap: 'wrap',
												}}
											>
												<div
													style={{
														background: sc.bg,
														border: `1px solid ${sc.border}`,
														padding: '4px 12px',
														fontFamily: 'DM Mono',
														fontSize: 9,
														color: sc.color,
														letterSpacing: '0.1em',
														textTransform: 'uppercase',
													}}
												>
													{req.Status}
												</div>
												<div
													style={{
														background: '#0D1816',
														border: '1px solid #1A2E2A',
														padding: '4px 12px',
														fontFamily: 'DM Mono',
														fontSize: 9,
														color: '#4A6A66',
														letterSpacing: '0.08em',
													}}
												>
													{req.Role || 'Partner'}
												</div>
											</div>
										</div>

										{/* Reason */}
										<div
											style={{
												background: '#0D1816',
												border: '1px solid #1A2E2A',
												padding: '14px 16px',
												marginBottom: isPending ? 20 : 0,
											}}
										>
											<div
												style={{
													fontFamily: 'DM Mono',
													fontSize: 9,
													color: '#4A6A66',
													letterSpacing: '0.1em',
													textTransform: 'uppercase',
													marginBottom: 6,
												}}
											>
												Reason for Access
											</div>
											<p
												style={{
													fontSize: 13,
													color: '#8AACAA',
													lineHeight: 1.7,
													margin: 0,
												}}
											>
												{req.Reason}
											</p>
										</div>

										{/* Timestamp */}
										<div
											style={{
												fontFamily: 'DM Mono',
												fontSize: 9,
												color: '#2A4A46',
												letterSpacing: '0.08em',
												marginTop: 12,
											}}
										>
											Requested{' '}
											{new Date(req.RequestedAt).toLocaleDateString('en-US', {
												year: 'numeric',
												month: 'long',
												day: 'numeric',
											})}
											{req.ProcessedAt &&
												` · Processed ${new Date(req.ProcessedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`}
										</div>

										{/* Actions — pending only */}
										{isPending && (
											<div
												style={{
													marginTop: 20,
													display: 'flex',
													flexDirection: 'column',
													gap: 12,
												}}
											>
												{/* Role selector + approve */}
												<div
													style={{
														display: 'flex',
														gap: 8,
														flexWrap: compact ? 'wrap' : 'nowrap',
														alignItems: 'center',
													}}
												>
													<div
														style={{
															display: 'flex',
															flexDirection: 'column',
															gap: 4,
															flex: 1,
														}}
													>
														<div
															style={{
																fontFamily: 'DM Mono',
																fontSize: 9,
																color: '#4A6A66',
																letterSpacing: '0.1em',
																textTransform: 'uppercase',
															}}
														>
															Assign Role
														</div>
														<select
															value={
																selectedRoles[req.RequestID] ||
																req.Role ||
																'Partner'
															}
															onChange={(e) =>
																setSelectedRoles((prev) => ({
																	...prev,
																	[req.RequestID]: e.target.value,
																}))
															}
															style={{
																background: '#0D1816',
																border: '1px solid #1A2E2A',
																padding: '9px 12px',
																color: 'white',
																fontSize: 12,
																fontFamily: 'inherit',
																outline: 'none',
																cursor: 'pointer',
															}}
														>
															{ROLE_OPTIONS.map((r) => (
																<option key={r} value={r}>
																	{r}
																</option>
															))}
														</select>
													</div>
													<button
														onClick={() => handleApprove(req)}
														disabled={!!isProcessing}
														style={{
															background: '#0E1F0E',
															border: '1px solid #6DBF67',
															cursor: 'pointer',
															padding: '10px 24px',
															fontFamily: 'DM Mono',
															fontSize: 10,
															color: '#6DBF67',
															letterSpacing: '0.1em',
															textTransform: 'uppercase',
															whiteSpace: 'nowrap',
															alignSelf: 'flex-end',
															opacity: isProcessing ? 0.5 : 1,
														}}
													>
														{isProcessing === 'approving'
															? 'Approving...'
															: '✓ Approve'}
													</button>
												</div>

												{/* Deny flow */}
												{!isDenyConfirming ? (
													<button
														onClick={() =>
															setConfirmingDeny((prev) => ({
																...prev,
																[req.RequestID]: true,
															}))
														}
														disabled={!!isProcessing}
														style={{
															background: 'transparent',
															border: '1px solid #3A2020',
															cursor: 'pointer',
															padding: '9px 24px',
															fontFamily: 'DM Mono',
															fontSize: 10,
															color: '#E07070',
															letterSpacing: '0.1em',
															textTransform: 'uppercase',
															opacity: isProcessing ? 0.5 : 1,
														}}
													>
														✕ Deny Request
													</button>
												) : (
													<div
														style={{
															background: '#1A0E0E',
															border: '1px solid #3A2020',
															padding: '16px',
														}}
													>
														<div
															style={{
																fontFamily: 'DM Mono',
																fontSize: 9,
																color: '#E07070',
																letterSpacing: '0.1em',
																textTransform: 'uppercase',
																marginBottom: 8,
															}}
														>
															Deny Reason (optional)
														</div>
														<textarea
															value={denyReasons[req.RequestID] || ''}
															onChange={(e) =>
																setDenyReasons((prev) => ({
																	...prev,
																	[req.RequestID]: e.target.value,
																}))
															}
															rows={2}
															placeholder='Reason for denial...'
															style={{
																width: '100%',
																background: '#0D1816',
																border: '1px solid #3A2020',
																padding: '10px 12px',
																color: 'white',
																fontSize: 12,
																fontFamily: 'inherit',
																outline: 'none',
																resize: 'vertical',
																boxSizing: 'border-box',
																marginBottom: 10,
															}}
														/>
														<div style={{ display: 'flex', gap: 8 }}>
															<button
																onClick={() => handleDeny(req)}
																disabled={!!isProcessing}
																style={{
																	background: '#2A1818',
																	border: '1px solid #E07070',
																	cursor: 'pointer',
																	padding: '8px 20px',
																	fontFamily: 'DM Mono',
																	fontSize: 10,
																	color: '#E07070',
																	letterSpacing: '0.1em',
																	textTransform: 'uppercase',
																	opacity: isProcessing ? 0.5 : 1,
																}}
															>
																{isProcessing === 'denying'
																	? 'Denying...'
																	: 'Confirm Deny'}
															</button>
															<button
																onClick={() =>
																	setConfirmingDeny((prev) => ({
																		...prev,
																		[req.RequestID]: false,
																	}))
																}
																style={{
																	background: 'transparent',
																	border: '1px solid #1A2E2A',
																	cursor: 'pointer',
																	padding: '8px 20px',
																	fontFamily: 'DM Mono',
																	fontSize: 10,
																	color: '#4A6A66',
																	letterSpacing: '0.1em',
																}}
															>
																Cancel
															</button>
														</div>
													</div>
												)}
											</div>
										)}

										{/* Deny reason display */}
										{req.Status === 'Denied' && req.DenyReason && (
											<div
												style={{
													marginTop: 12,
													fontFamily: 'DM Mono',
													fontSize: 10,
													color: '#4A6A66',
													letterSpacing: '0.06em',
												}}
											>
												Deny reason:{' '}
												<span style={{ color: '#6A8A86' }}>
													{req.DenyReason}
												</span>
											</div>
										)}
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
